
import {
  defineComponent,
  ref,
  onMounted,
  nextTick,
  PropType,
} from '@vue/composition-api';
import { IconButton } from '@fc/angie-ui';
import { isMobileEffect } from '~/effects/matchMedia';

interface SwiperBreakpoint {
  slidesPerView: number;
  spaceBetween: number;
  centeredSlides?: boolean;
}

interface SwiperBreakpoints {
  [key: number]: SwiperBreakpoint;
}

export default defineComponent({
  name: 'FCSwiper',
  components: {
    IconButton,
  },
  props: {
    slidesPerView: {
      type: Number,
      default: 1,
    },
    slidesPerGroup: {
      type: Number,
      default: 1,
    },
    spaceBetween: {
      type: Number,
      default: 16,
    },
    centeredSlides: {
      type: Boolean,
      default: false,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    autoplay: {
      type: [Boolean, Object] as PropType<boolean | { delay: number; disableOnInteraction?: boolean }>,
      default: false,
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
    showNavigation: {
      type: Boolean,
      default: false,
    },
    showProgressBar: {
      type: Boolean,
      default: false,
    },
    progressBarDuration: {
      type: Number,
      default: 5000,
    },
    navigationPosition: {
      type: String as PropType<'center' | 'bottom'>,
      default: 'bottom',
    },
    navigationAlignment: {
      type: String as PropType<'left' | 'center' | 'right'>,
      default: 'center',
    },
    breakpoints: {
      type: Object as PropType<SwiperBreakpoints>,
      default: null,
    },
    labelForAria: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const swiperRef = ref<any>(null);
    const progressBar = ref<HTMLElement | null>(null);
    const currentSlide = ref(0);
    const totalSlides = ref(0);
    const isBeginning = ref(true);
    const isEnd = ref(false);

    const initializeSwiper = async () => {
      await nextTick();
      const swiperEl = swiperRef.value;
      if (!swiperEl) return;

      const swiperParams: any = {
        slidesPerView: props.slidesPerView,
        slidesPerGroup: props.slidesPerGroup,
        spaceBetween: props.spaceBetween,
        centeredSlides: props.centeredSlides,
        loop: props.loop,
      };

      // Disable Swiper's built-in pagination - we'll use our own
      swiperParams.pagination = false;

      // Add autoplay if enabled
      if (props.autoplay) {
        if (typeof props.autoplay === 'object') {
          swiperParams.autoplay = props.autoplay;
        } else {
          swiperParams.autoplay = {
            delay: props.progressBarDuration,
            disableOnInteraction: false,
          };
        }
      }

      // Add breakpoints if provided
      if (props.breakpoints) {
        swiperParams.breakpoints = props.breakpoints;
      }

      Object.assign(swiperEl, swiperParams);
      swiperEl.initialize();

      // Set up event listeners
      if (swiperEl.swiper) {
        totalSlides.value = swiperEl.swiper.slides.length;
        currentSlide.value = swiperEl.swiper.realIndex;
        isBeginning.value = swiperEl.swiper.isBeginning;
        isEnd.value = swiperEl.swiper.isEnd;

        swiperEl.swiper.on('slideChange', () => {
          currentSlide.value = swiperEl.swiper.realIndex;
          isBeginning.value = swiperEl.swiper.isBeginning;
          isEnd.value = swiperEl.swiper.isEnd;

          // Emit slidechange event for parent components
          emit('slidechange', {
            activeIndex: swiperEl.swiper.realIndex,
            isBeginning: swiperEl.swiper.isBeginning,
            isEnd: swiperEl.swiper.isEnd,
          });
        });
      }
    };

    const handlePrevSlide = () => {
      const swiperEl = swiperRef.value;
      if (swiperEl && swiperEl.swiper) {
        swiperEl.swiper.slidePrev();
      }
    };

    const handleNextSlide = () => {
      const swiperEl = swiperRef.value;
      if (swiperEl && swiperEl.swiper) {
        swiperEl.swiper.slideNext();
      }
    };

    const goToSlide = (index: number) => {
      const swiperEl = swiperRef.value;
      if (swiperEl && swiperEl.swiper) {
        swiperEl.swiper.slideToLoop(index);
      }
    };

    onMounted(() => {
      initializeSwiper();
    });

    const isMobile = isMobileEffect();

    return {
      swiperRef,
      progressBar,
      currentSlide,
      totalSlides,
      isBeginning,
      isEnd,
      handlePrevSlide,
      handleNextSlide,
      goToSlide,
      isMobile,
    };
  },
});
